import { motion } from "framer-motion";
import { Users, MessageSquare, Shield, Zap } from "lucide-react";
import { useStats } from "@/hooks/useStats";

const StatsSection = () => {
  const { stats, loading } = useStats();

  // Format numbers with K, M suffix
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M+`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K+`;
    }
    return num.toString();
  };

  const statsData = [
    {
      icon: Users,
      value: loading ? "..." : formatNumber(stats?.servers || 0),
      label: "Discord Servers",
      color: "from-primary to-cyan-400",
    },
    {
      icon: MessageSquare,
      value: loading ? "..." : formatNumber(stats?.messages || 0),
      label: "Messages Processed",
      color: "from-secondary to-pink-400",
    },
    {
      icon: Shield,
      value: loading ? "..." : `${stats?.uptime.toFixed(1) || 0}%`,
      label: "Uptime",
      color: "from-accent to-emerald-400",
    },
    {
      icon: Zap,
      value: loading ? "..." : `${stats?.responseTime || 0}ms`,
      label: "Response Time",
      color: "from-discord-yellow to-orange-400",
    },
  ];

  return (
    <section className="py-20 px-6 relative">
      <div className="container mx-auto max-w-6xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {statsData.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="stat-card group hover:border-primary/30 transition-all duration-300"
            >
              <div className={`w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold gradient-text mb-1">{stat.value}</h3>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
